"use client"

import { useState } from "react"
import { CompanyUpdate, CompanyUpdateStatus, formatDate, getImageUrl, getSortAndOrderBy, TABLE_TAKE } from "@repo/utils"
import { useBusinessCompanyUpdates, useDeleteCompanyUpdateMutation, useUserBusinessesQuery } from "@repo/core/src/services/businesses"
import { useProPortalInfoQuery } from "@repo/core/src/services/pro-portals"
import CompanyUpdateModal from "./company-update-modal"
import { deleteFile } from "@repo/core/src/lib/s3"
import { permissionCheck } from "../form"
import { GenericDropdownActions, Loading, Table } from "../common"
import PermissionCheck from "../form/roles/permission-check"
import { useDebounce } from "../../hooks"
import TableSortSelector from "../table-sort-selector"
import { TableSearchInput } from ".."

const take: number = TABLE_TAKE

const CompanyUpdatesTable = () => {
  const [selectedCompanyUpdate, setSelectedCompanyUpdate] = useState<CompanyUpdate | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(1)
const [searchTerm, setSearchTerm] = useState("")
  const [selected, setSelected] = useState('name_asc')

  const { debouncedValue } = useDebounce(searchTerm, 500)

  const skip: number = (currentPage - 1) * take

  const { sortBy, orderBy } = getSortAndOrderBy(selected)
  const { data: proPortal } = useProPortalInfoQuery()
  const { data: companyUpdatesData, isLoading } = useBusinessCompanyUpdates({ uuid: proPortal?.business?.uuid, skip, take, search: debouncedValue, orderBy, sortBy })
  const { data: userWithBusinesses } = useUserBusinessesQuery()

  const { mutate } = useDeleteCompanyUpdateMutation(proPortal?.business?.uuid ?? "UUID_MISSING")

  const companyUpdates = companyUpdatesData?.companyUpdates || []
  const totalPages = companyUpdatesData?.totalPages || 1
  const totalUpdates = companyUpdatesData?.totalCount || 0

  const goToPage = (page: number): void => {
    setCurrentPage(page)
  }

  const businessId = proPortal?.businessId ?? 0

  const hasAccess =
    userWithBusinesses &&
    permissionCheck({
      user: userWithBusinesses,
      action: "business:updates",
      businessId,
      isProPortal: true,
      proPortalId: proPortal?.id,
    })

  const companyUpdateStatusColor = (status: CompanyUpdateStatus) => {
    switch (status) {
      case "DRAFT":
        return "text-gray-500 bg-[#E5E7EB] text-[12px] rounded-full px-2.5 py-0.5"
      case "PUBLISHED":
        return "text-white bg-[#15803D] text-[12px] rounded-full px-2.5 py-0.5"
      default:
        return "text-black bg-[#ffc107] text-[12px] rounded-full px-2.5 py-0.5"
    }
  }

  const tableTitleActions = (
    <div className="flex items-center gap-2">
      <TableSortSelector selected={selected} setSelected={setSelected} tableSortType="CompanyUpdates" />

      <TableSearchInput value={searchTerm} setValue={setSearchTerm} />
    </div>
  )

  return (
    <>
      <Table
        isRounded
        pageItems={companyUpdates || []}
        title="All Updates"
        titleActions={tableTitleActions}
        itemKey="id"
        itemTableRowProps={{ className: "grid grid-cols-1 sm:grid-cols-7 md:grid-cols-8 xl:grid-cols-11 gap-2" }}
        headerTableRowProps={{ className: "grid grid-cols-1 sm:grid-cols-7 md:grid-cols-8 xl:grid-cols-11 gap-2" }}
        tableLayout="table-auto"
        totalItems={totalUpdates}
        pagination={{
          goToPage: (page: number) => {
            if (goToPage) goToPage(page)
          },
          numberOfPages: totalPages,
          defaultPageNumber: currentPage,
        }}
        isLoading={isLoading}
        columns={[
          {
            label: {
              key: "id",
              title: "Updates",
              className: "whitespace-normal break-words col-span-3 sm:col-span-5",
            },
            value: {
              key: "id",
              className: "whitespace-normal break-words col-span-3 sm:col-span-5 items-center flex",
              render: (item: CompanyUpdate) => (
                <div className="flex items-center gap-2 w-full">
                  <img src={getImageUrl(item.image?.key ?? "")} className="w-[78px] h-[56px]" />{" "}
                  <div className="min-w-[200px] max-w-[400px] text-left">
                    <p className="text-sm font-semibold truncate">{item.name}</p>
                    <p className="text-sm truncate">{item.message}</p>
                  </div>
                </div>
              ),
            },
          },
          {
            label: {
              key: "tag",
              title: "Tags",
              className: "hidden sm:flex col-span-1",
            },
            value: {
              key: "tag",
              className: "hidden sm:flex col-span-1 items-center flex",
              render: (item: CompanyUpdate) =>
                item.tag && (
                  <div className="hidden sm:flex rounded-full text-gray-500 bg-[#E5E7EB] text-[12px] px-3 py-1">
                    <p className="truncate">{item.tag}</p>
                  </div>
                ),
            },
          },
          {
            label: {
              key: "category",
              title: "Category",
              className: "hidden xl:flex col-span-1",
            },
            value: {
              key: "category",
              className: "hidden xl:flex col-span-1 items-center flex",
              render: (item: CompanyUpdate) =>
                item.category && (
                  <div className="hidden xl:flex rounded-lg bg-blue-500 text-white px-3 py-1">
                    <p className="truncate">{item.category}</p>
                  </div>
                ),
            },
          },
          {
            label: {
              key: "updated",
              title: "Date",
              className: "hidden xl:flex col-span-1",
            },
            value: {
              className: "hidden xl:flex col-span-1 items-center flex",
              render: (item: CompanyUpdate) => <p className="hidden xl:flex">{formatDate(item.updated?.toLocaleString() ?? "")}</p>,
            },
          },
          {
            label: {
              key: "views",
              title: "Views",
              className: "hidden md:flex col-span-1 justify-start md:justify-center",
            },
            value: {
              key: "views",
              className: "hidden md:flex col-span-1 items-center flex justify-start md:justify-center",
              render: (item: CompanyUpdate) => <p className="text-sm hidden md:flex">{item.views}</p>,
            },
          },
          {
            label: {
              key: "status",
              title: "Status",
              className: "hidden xl:flex col-span-1 justify-start md:justify-center",
            },
            value: {
              key: "status",
              className: "hidden xl:flex col-span-1 items-center flex justify-start md:justify-center",
              render: (item: CompanyUpdate) => {
                const status = item.status ?? ''
                return (
                <div className={`hidden xl:flex rounded-xl px-2 py-1 ${companyUpdateStatusColor(item.status ?? "DRAFT")}`}>
                  <p>{status.slice(0,1)}{status.slice(1).toLowerCase()}</p>
                </div>
              )},
            },
          },
          {
            label: {
              key: "id",
              title: "Actions",
              className: "hidden sm:flex col-span-1 justify-start md:justify-center",
            },
            value: {
              key: "id",
              className: "col-span-1 items-center hidden sm:flex justify-end",
              render: (item: CompanyUpdate) => (
                <PermissionCheck
                  action="business:updates"
                  user={userWithBusinesses}
                  businessId={userWithBusinesses?.selectedBusiness?.id}
                  isProPortal={true}
                  proPortalId={proPortal?.id}
                  yes={() => (
                    <div className="flex flex-row items-center justify-end gap-2 w-full h-full">
                      <GenericDropdownActions
                        align="right"
                        onEdit={() => {
                          setSelectedCompanyUpdate(item)
                        }}
                        disableEdit={!hasAccess}
                        onDelete={async () => {
                          if (item.image?.key) {
                            await deleteFile(item.image.key)
                          }
                          mutate(item.uuid)
                        }}
                        disableDelete={!hasAccess}
                      />
                    </div>
                  )}
                  no={() => <></>}
                />
              ),
            },
          },
        ]}
      />

      {selectedCompanyUpdate && (
        <CompanyUpdateModal
          isOpen={selectedCompanyUpdate ? true : false}
          setIsOpen={() => setSelectedCompanyUpdate(null)}
          companyUpdate={selectedCompanyUpdate}
          isEdit={true}
        />
      )}
    </>
  )
}

export default CompanyUpdatesTable
